'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { toast } from 'sonner'
import {
  CalendarDays, PartyPopper, Moon, Plane, Home, ClipboardList, Plus, Trash2, Pencil,
  MapPin, Clock, Users as UsersIcon, Loader2, CalendarRange,
} from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { DateInput } from '@/components/ui/date-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { StatusPill } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { MonthGrid } from '@/components/ui/month-grid'
import { PRIMARY_BTN } from '@/components/ui/button-styles'
import { todayIST, formatISTDate } from '@/lib/date-utils'
import { EVENT_TYPES } from '@/lib/validators'
import { eventsByDay, formatEventTime, type CalendarEvent } from '@/lib/events'
import { cn } from '@/lib/utils'

interface Holiday { id: string; date: string; name: string }
interface DirectoryUser { id: string; name: string; department: string | null; role: string; isActive?: boolean }
interface TaskLite { id: string; title: string; dueDate: string | null; status: string; assignedTo?: string[] | string | null }

type ItemKind = 'HOLIDAY' | 'EVENT' | 'LEAVE' | 'WFH' | 'TASK'

interface DayItem {
  kind: ItemKind
  label: string
  detail?: string
  event?: CalendarEvent
}

const KIND_META: Record<ItemKind, { label: string; dot: string; chip: string; icon: typeof CalendarDays }> = {
  HOLIDAY: {
    label: 'Holiday',
    dot: 'bg-cyan-500',
    chip: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-200',
    icon: PartyPopper,
  },
  EVENT: {
    label: 'Event',
    dot: 'bg-orange-500',
    chip: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-200',
    icon: CalendarDays,
  },
  LEAVE: {
    label: 'Leave',
    dot: 'bg-amber-500',
    chip: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
    icon: Plane,
  },
  WFH: {
    label: 'WFH',
    dot: 'bg-violet-500',
    chip: 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200',
    icon: Home,
  },
  TASK: {
    label: 'Task due',
    dot: 'bg-blue-500',
    chip: 'bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200',
    icon: ClipboardList,
  },
}

const EMPTY_FORM = {
  id: '' as string,
  title: '',
  type: 'EVENT' as string,
  startDate: '',
  endDate: '',
  allDay: true,
  startTime: '',
  endTime: '',
  location: '',
  description: '',
  audience: 'ALL' as 'ALL' | 'SELECTED',
  attendees: [] as string[],
}

interface TeamCalendarProps {
  /** The viewer — their leave, WFH and task deadlines form the personal layer. */
  userId: string
  /** Admin/HR: can create, edit and delete events. */
  canManage?: boolean
}

/**
 * The shared company calendar, rendered for every role. Layers company-wide
 * entries (events, holidays) with the viewer's own schedule (approved leave and
 * WFH, task deadlines). Admin/HR additionally get event management here.
 */
export function TeamCalendar({ userId, canManage = false }: TeamCalendarProps) {
  const [month, setMonth] = useState(() => todayIST().slice(0, 7))
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [leaveDays, setLeaveDays] = useState<Set<string>>(new Set())
  const [wfhDays, setWfhDays] = useState<Set<string>>(new Set())
  const [tasks, setTasks] = useState<TaskLite[]>([])
  const [staff, setStaff] = useState<DirectoryUser[]>([])

  const [openDate, setOpenDate] = useState<string | null>(null)
  const [form, setForm] = useState<typeof EMPTY_FORM | null>(null)
  const [saving, setSaving] = useState(false)

  const monthBounds = useMemo(() => {
    const [y, m] = month.split('-').map(Number)
    const lastDay = new Date(y, m, 0).getDate()
    return { start: `${month}-01`, end: `${month}-${String(lastDay).padStart(2, '0')}` }
  }, [month])

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const asJson = async (p: Promise<Response>) => (await p).json()
      // Settled, not all: a failing side endpoint must not blank the calendar.
      const [ev, hol, lv, wf, tk, us] = await Promise.allSettled([
        asJson(apiFetch(`/api/events?startDate=${monthBounds.start}&endDate=${monthBounds.end}`)),
        asJson(apiFetch('/api/holidays')),
        asJson(apiFetch('/api/leave')),
        asJson(apiFetch('/api/wfh')),
        asJson(apiFetch('/api/tasks')),
        canManage ? asJson(apiFetch('/api/users')) : Promise.resolve({ users: [] }),
      ])

      if (ev.status === 'fulfilled' && ev.value?.events) setEvents(ev.value.events)
      else if (ev.status === 'rejected') toast.error(ev.reason?.message || 'Failed to load events')

      if (hol.status === 'fulfilled' && hol.value?.holidays) setHolidays(hol.value.holidays)

      // Admin/HR receive every request from these endpoints, so the personal
      // layer is filtered to the viewer here rather than trusting the response.
      const mine = (rows: any[]) => {
        const out = new Set<string>()
        for (const r of rows || []) {
          if (String(r?.status ?? '').toUpperCase() !== 'APPROVED') continue
          if ((r.userId ?? r.employee_id) !== userId) continue
          const start = r.startDate ?? r.from_date
          const end = r.endDate ?? r.to_date
          if (!start || !end) continue
          for (let d = new Date(start + 'T00:00:00'); d <= new Date(end + 'T00:00:00'); d.setDate(d.getDate() + 1)) {
            out.add(formatISTDate(d))
          }
        }
        return out
      }
      if (lv.status === 'fulfilled') setLeaveDays(mine(lv.value?.leaves))
      if (wf.status === 'fulfilled') setWfhDays(mine(wf.value?.wfhRequests))

      if (tk.status === 'fulfilled' && Array.isArray(tk.value?.tasks)) {
        setTasks(tk.value.tasks.filter((t: TaskLite) => {
          if (!t?.dueDate || t.status === 'COMPLETED' || t.status === 'CANCELLED') return false
          const assigned = Array.isArray(t.assignedTo) ? t.assignedTo : t.assignedTo ? [t.assignedTo] : []
          return assigned.includes(userId)
        }))
      }

      if (us.status === 'fulfilled' && Array.isArray(us.value?.users)) {
        setStaff(us.value.users.filter((u: DirectoryUser) => u && u.isActive !== false))
      }
    } catch (e: any) {
      console.error('Error loading calendar:', e)
      toast.error(e?.message || 'Failed to load calendar')
    } finally {
      setLoading(false)
    }
  }, [monthBounds.start, monthBounds.end, userId, canManage])

  useEffect(() => { fetchData() }, [fetchData])

  const holidayByDate = useMemo(() => {
    const map: Record<string, Holiday> = {}
    for (const h of holidays) if (h?.date) map[h.date] = h
    return map
  }, [holidays])

  const eventsForDay = useMemo(() => eventsByDay(events), [events])

  const tasksByDay = useMemo(() => {
    const map: Record<string, TaskLite[]> = {}
    for (const t of tasks) {
      const due = (t.dueDate || '').slice(0, 10)
      if (!due) continue
      if (!map[due]) map[due] = []
      map[due].push(t)
    }
    return map
  }, [tasks])

  /** Everything scheduled on a given day, in the order it should be listed. */
  const itemsForDay = useCallback((date: string): DayItem[] => {
    const out: DayItem[] = []
    const holiday = holidayByDate[date]
    if (holiday) out.push({ kind: 'HOLIDAY', label: holiday.name })
    for (const e of eventsForDay[date] || []) {
      out.push({ kind: 'EVENT', label: e.title, detail: formatEventTime(e), event: e })
    }
    if (leaveDays.has(date)) out.push({ kind: 'LEAVE', label: 'You are on leave' })
    if (wfhDays.has(date)) out.push({ kind: 'WFH', label: 'Work from home' })
    for (const t of tasksByDay[date] || []) {
      out.push({ kind: 'TASK', label: t.title, detail: 'Due today' })
    }
    return out
  }, [holidayByDate, eventsForDay, leaveDays, wfhDays, tasksByDay])

  /** Next 30 days of scheduled items, for the agenda under the grid. */
  const upcoming = useMemo(() => {
    const today = todayIST()
    const out: { date: string; items: DayItem[] }[] = []
    for (let i = 0; i < 30; i++) {
      const d = new Date(today + 'T00:00:00Z')
      d.setUTCDate(d.getUTCDate() + i)
      const date = d.toISOString().slice(0, 10)
      const items = itemsForDay(date)
      if (items.length) out.push({ date, items })
      if (out.length >= 6) break
    }
    return out
  }, [itemsForDay])

  const openCreate = (date?: string) => {
    const d = date || todayIST()
    setForm({ ...EMPTY_FORM, startDate: d, endDate: d })
  }

  const openEdit = (event: CalendarEvent) => {
    setForm({
      id: event.id,
      title: event.title,
      type: event.type,
      startDate: event.startDate,
      endDate: event.endDate,
      allDay: !event.startTime,
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      location: event.location || '',
      description: event.description || '',
      audience: (event.audience === 'SELECTED' ? 'SELECTED' : 'ALL'),
      attendees: event.attendees || [],
    })
  }

  const saveEvent = async () => {
    if (!form) return
    if (!form.title.trim()) return toast.error('Give the event a title')
    if (!form.startDate) return toast.error('Pick a start date')
    if (form.endDate && form.endDate < form.startDate) return toast.error('End date cannot be before the start date')
    if (form.audience === 'SELECTED' && form.attendees.length === 0) {
      return toast.error('Select at least one employee, or make it company-wide')
    }

    setSaving(true)
    try {
      const body = {
        title: form.title.trim(),
        type: form.type,
        startDate: form.startDate,
        endDate: form.endDate || form.startDate,
        startTime: form.allDay ? null : form.startTime || null,
        endTime: form.allDay ? null : form.endTime || null,
        location: form.location.trim() || null,
        description: form.description.trim() || null,
        audience: form.audience,
        attendees: form.audience === 'SELECTED' ? form.attendees : [],
      }
      await apiFetch(form.id ? `/api/events/${form.id}` : '/api/events', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      toast.success(form.id ? 'Event updated' : 'Event added to the calendar')
      setForm(null)
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to save event')
    } finally {
      setSaving(false)
    }
  }

  const deleteEvent = async (event: CalendarEvent) => {
    if (!confirm(`Remove "${event.title}" from the calendar?`)) return
    try {
      await apiFetch(`/api/events/${event.id}`, { method: 'DELETE' })
      toast.success('Event removed')
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to remove event')
    }
  }

  const selectedItems = openDate ? itemsForDay(openDate) : []

  return (
    <>
      <SectionHeader
        icon={CalendarDays}
        title="Calendar"
        description="Company events, holidays and everything scheduled for you"
        action={
          canManage ? (
            <Button className={PRIMARY_BTN} onClick={() => openCreate()}>
              <Plus className="mr-2 h-4 w-4" />
              Add event
            </Button>
          ) : undefined
        }
      />

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : (
          <MonthGrid
            month={month}
            onMonthChange={setMonth}
            onDayClick={(d) => setOpenDate(d)}
            hint={canManage ? 'Click a day to see or add what is scheduled.' : 'Click a day to see what is scheduled.'}
            toolbar={
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500 dark:text-white/50">
                {(Object.keys(KIND_META) as ItemKind[]).map(k => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className={cn('h-1.5 w-1.5 rounded-full', KIND_META[k].dot)} />
                    {KIND_META[k].label}
                  </span>
                ))}
              </div>
            }
            renderDay={(date, meta) => {
              const items = itemsForDay(date)
              const isHoliday = items.some(i => i.kind === 'HOLIDAY')
              const className = isHoliday
                ? 'bg-cyan-50 ring-cyan-200 hover:ring-cyan-300 dark:bg-cyan-500/10 dark:ring-cyan-500/25 dark:hover:ring-cyan-500/40'
                : items.length
                  ? 'bg-orange-50/70 ring-orange-200 hover:ring-orange-300 dark:bg-orange-500/10 dark:ring-orange-500/25 dark:hover:ring-orange-500/40'
                  : meta.isWeekend
                    ? 'bg-neutral-50 ring-neutral-200/70 dark:bg-white/[0.02] dark:ring-white/5'
                    : 'bg-transparent ring-neutral-200/60 dark:ring-white/5'

              if (!items.length) {
                return {
                  title: date,
                  className,
                  content: meta.isWeekend
                    ? <Moon className="h-4 w-4 text-neutral-300 dark:text-white/20 sm:h-[18px] sm:w-[18px]" />
                    : undefined,
                }
              }

              return {
                title: `${date} · ${items.map(i => i.label).join(' · ')}`,
                className,
                content: (
                  <div className="mt-3.5 flex w-full flex-col gap-0.5 px-0.5">
                    {items.slice(0, 2).map((item, i) => (
                      <span
                        key={i}
                        className={cn('truncate rounded px-1 text-[8px] leading-tight sm:text-[9px]', KIND_META[item.kind].chip)}
                      >
                        {item.label}
                      </span>
                    ))}
                    {items.length > 2 && (
                      <span className="text-[8px] leading-tight text-neutral-500 dark:text-white/50 sm:text-[9px]">
                        +{items.length - 2} more
                      </span>
                    )}
                  </div>
                ),
              }
            }}
          />
        )}
      </GlassCard>

      {/* Agenda — the same data as a forward-looking list, so nothing needs to
          be discovered by clicking through days. */}
      <GlassCard>
        <div className="mb-3 flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-orange-500 dark:text-orange-300" />
          <h3 className="text-sm font-semibold tracking-tight">Coming up</h3>
        </div>
        {loading ? (
          <TabLoader />
        ) : upcoming.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title="Nothing scheduled"
            description="Events, holidays, your leave and task deadlines for the next 30 days appear here."
          />
        ) : (
          <ul className="space-y-3">
            {upcoming.map(({ date, items }) => (
              <li key={date} className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setOpenDate(date)}
                  className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-neutral-100 text-neutral-700 ring-1 ring-inset ring-neutral-200 transition-colors hover:bg-neutral-200 dark:bg-white/5 dark:text-white/70 dark:ring-white/10 dark:hover:bg-white/10"
                >
                  <span className="text-[10px] uppercase tracking-wider">
                    {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                  </span>
                  <span className="text-sm font-semibold tabular-nums leading-none">
                    {Number(date.slice(8, 10))}
                  </span>
                </button>
                <div className="min-w-0 flex-1 space-y-1 py-0.5">
                  {items.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 min-w-0">
                      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', KIND_META[item.kind].dot)} />
                      <span className="truncate text-sm text-neutral-800 dark:text-white/80">{item.label}</span>
                      {item.detail && (
                        <span className="shrink-0 text-[11px] text-neutral-400 dark:text-white/40">{item.detail}</span>
                      )}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>

      {/* Day detail */}
      <Dialog open={!!openDate} onOpenChange={(open) => !open && setOpenDate(null)}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 max-w-lg w-full text-neutral-900 dark:text-neutral-100">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">
              {openDate && new Date(openDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              {selectedItems.length === 0
                ? 'Nothing scheduled for this day'
                : `${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'} scheduled`}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {selectedItems.map((item, i) => {
              const meta = KIND_META[item.kind]
              const Icon = meta.icon
              return (
                <div
                  key={i}
                  className="flex items-start justify-between gap-3 rounded-xl bg-neutral-50 px-3 py-2.5 ring-1 ring-inset ring-neutral-200 dark:bg-white/[0.03] dark:ring-white/10"
                >
                  <div className="flex min-w-0 gap-2.5">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400 dark:text-white/40" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-neutral-900 dark:text-white/90">{item.label}</p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-neutral-500 dark:text-white/50">
                        <StatusPill tone="neutral">{meta.label}</StatusPill>
                        {item.detail && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{item.detail}</span>}
                        {item.event?.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{item.event.location}</span>}
                        {item.event?.audience === 'SELECTED' && (
                          <span className="inline-flex items-center gap-1">
                            <UsersIcon className="h-3 w-3" />{item.event.attendees?.length || 0} invited
                          </span>
                        )}
                      </div>
                      {item.event?.description && (
                        <p className="mt-1.5 whitespace-pre-wrap text-xs text-neutral-600 dark:text-white/60">{item.event.description}</p>
                      )}
                    </div>
                  </div>
                  {canManage && item.event && (
                    <div className="flex shrink-0 items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { openEdit(item.event!); setOpenDate(null) }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-rose-600 hover:bg-rose-500/10 dark:text-rose-400"
                        onClick={() => deleteEvent(item.event!)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {canManage && (
            <DialogFooter>
              <Button
                className={PRIMARY_BTN}
                onClick={() => { const d = openDate; setOpenDate(null); openCreate(d || undefined) }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add event on this day
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / edit — Admin & HR only */}
      <Dialog open={!!form} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 max-w-lg w-full">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white">{form?.id ? 'Edit event' : 'New event'}</DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              Appears on the calendar of everyone it applies to.
            </DialogDescription>
          </DialogHeader>

          {form && (
            <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Quarterly town hall"
                  maxLength={140}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={form.location}
                    onChange={e => setForm({ ...form, location: e.target.value })}
                    placeholder="Office / Google Meet"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Start date</Label>
                  <DateInput
                    value={form.startDate}
                    onChange={v => setForm({ ...form, startDate: v, endDate: form.endDate && form.endDate >= v ? form.endDate : v })}
                    placeholder="Select start date"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End date</Label>
                  <DateInput
                    value={form.endDate}
                    onChange={v => setForm({ ...form, endDate: v })}
                    placeholder="Same day"
                    minDate={form.startDate || undefined}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="event-all-day"
                  checked={form.allDay}
                  onCheckedChange={c => setForm({ ...form, allDay: c === true })}
                />
                <Label htmlFor="event-all-day" className="cursor-pointer">All day</Label>
              </div>

              {!form.allDay && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Start time</Label>
                    <Input type="time" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>End time</Label>
                    <Input type="time" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Who is it for?</Label>
                <Select
                  value={form.audience}
                  onValueChange={v => setForm({ ...form, audience: v as 'ALL' | 'SELECTED' })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Everyone</SelectItem>
                    <SelectItem value="SELECTED">Selected employees</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.audience === 'SELECTED' && (
                <ScrollArea className="h-44 rounded-xl ring-1 ring-inset ring-neutral-200 dark:ring-white/10">
                  <div className="space-y-1 p-2">
                    {staff.length === 0 && (
                      <p className="p-2 text-xs text-neutral-500 dark:text-white/50">No employees found.</p>
                    )}
                    {staff.map(u => {
                      const checked = form.attendees.includes(u.id)
                      return (
                        <label
                          key={u.id}
                          className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-neutral-100 dark:hover:bg-white/5"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={c => setForm({
                              ...form,
                              attendees: c === true
                                ? [...form.attendees, u.id]
                                : form.attendees.filter(id => id !== u.id),
                            })}
                          />
                          <span className="truncate text-sm text-neutral-800 dark:text-white/80">{u.name}</span>
                          {u.department && (
                            <span className="ml-auto shrink-0 text-[11px] text-neutral-400 dark:text-white/40">{u.department}</span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                </ScrollArea>
              )}

              <div className="space-y-2">
                <Label>Details</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder="Agenda, joining link, anything people need to know..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="border-neutral-200 dark:border-white/10" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button onClick={saveEvent} disabled={saving} className={PRIMARY_BTN}>
              {saving ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>) : form?.id ? 'Save changes' : 'Add event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
